import React, { useState, useEffect, memo, ChangeEvent, SyntheticEvent, useCallback } from 'react'
import { FormProvider, useWatch } from 'react-hook-form'
import compact from 'lodash/compact'
import omit from 'lodash/omit'
import moment from 'moment-timezone'
import ControlledRadioGroupInput from 'transfix-form/components/ControlledRadioGroupInput'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import RemoveIcon from 'transfix-icons/build/RemoveIcon'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage } from 'transfix-ui/components/InlineMessage/InlineMessage'
import { FormModal } from 'transfix-ui/components/Modals'
import { Radio } from 'transfix-ui/components/Radio/Radio'
import { RangeDatePicker } from 'transfix-ui/components/RangeDatePicker/RangeDatePicker'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { Box, Typography } from 'transfix-ui/core'
import useCarrier from 'utils/hooks/useCarrier'
import {
  useCreateLaneAlert,
  useUpdateCMLaneAlert,
  LaneAlert,
} from 'apps/marketplace/apiTypes/carrierLaneAlerts'
import AdminAutosuggest from 'apps/marketplace/components/AdminAutosuggest'
import CarrierAutosuggest from 'apps/marketplace/components/CarrierAutosuggest/LegacyCarrierAutosuggest'
import { ISpecificLane } from 'apps/marketplace/store/routingGuide/types'
import { INameSearchResult, SEARCH_TYPE } from 'apps/marketplace/utils/api/search'
import { Checkbox } from 'components/forms'
import { FORMATS } from 'constants/datetime'
import { TAddLaneAlertModalSchema } from './AddLaneAlertModalSchema'
import {
  daysOfWeek,
  dispatcherScopeOptions,
  DispatcherScope,
  DropoffPreference,
  EquipmentType,
  ICarrierObjectAddLaneModal,
  stopRadii,
  SubscribedDispatcher,
  TODAY,
  trailerTypeOptions,
} from './constants'
import { deriveDropoffPref } from './helpers'
import { useAddLaneAlertModalForm } from './useAddLaneAlertModalForm'

interface IAddLaneAlertModalProps {
  carriers?: ICarrierObjectAddLaneModal[]
  carrierId?: number
  carrierName?: string
  laneAlert?: LaneAlert
  specificLane?: ISpecificLane
  closeModal: () => void
  onSuccess: () => void
}
export const AddLaneAlertModal = memo<IAddLaneAlertModalProps>(
  ({ closeModal, onSuccess, carriers = [], laneAlert, carrierId, carrierName, specificLane }) => {
    const isAdd = laneAlert === undefined
    // Lane Alert
    const {
      deliveryCity,
      deliveryStateCode,
      loadedMilesMax,
      loadedMilesMin,
      pickupAvailability,
      pickupCity,
      pickupEndDate,
      pickupIsoDaysOfWeek: pickupIsoDays,
      pickupStartDate,
      pickupStateCode,
      recipientSubscribers,
      trailerType,
    } = laneAlert || {}
    const pickupIsoDaysOfWeek = pickupIsoDays !== null ? pickupIsoDays : [1, 2, 3, 4, 5, 6, 7]

    // Specific Lane
    const { deliveryCityState, equipment, pickupCityState } = specificLane || {}
    const specificLanePickup = pickupCityState
      ? pickupCityState.split(',').map(v => v.trim())
      : undefined
    const specificLaneDelivery = deliveryCityState
      ? deliveryCityState.split(',').map(v => v.trim())
      : undefined
    const [specificLanePickupCity, specificLanePickupState] = specificLanePickup || []
    const [specificLaneDeliveryCity, specificLaneDeliveryState] = specificLaneDelivery || []
    const { key: specificLaneTrailerType } = equipment || {}

    const methods = useAddLaneAlertModalForm({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        trailerType: isAdd
          ? specificLaneTrailerType || undefined
          : trailerType || EquipmentType.any,
        pickupCity: isAdd ? specificLanePickupCity : pickupCity,
        pickupStateCode: isAdd ? specificLanePickupState : pickupStateCode,
        deliveryCity: isAdd ? specificLaneDeliveryCity || null : deliveryCity || null,
        deliveryStateCode: isAdd ? specificLaneDeliveryState || null : deliveryStateCode || null,
        loadedMilesMin: loadedMilesMin || null,
        loadedMilesMax: loadedMilesMax || null,
        pickupIsoDaysOfWeek,
        pickupStartDate,
        pickupEndDate: pickupEndDate || null,
        subscriberParentIds: recipientSubscribers?.map(dispr => dispr.parentUserId) || [],
        dispatcherScope: isAdd ? undefined : DispatcherScope.specificDispatchers,
      },
    })

    const initialDateRange = compact([
      pickupStartDate ? moment(pickupStartDate).toDate() : null,
      pickupEndDate ? moment(pickupEndDate).toDate() : null,
    ])
    const defaultSubscribers: SubscribedDispatcher[] =
      recipientSubscribers?.map(({ email, firstName = '', lastName = '', parentUserId }) => ({
        id: String(parentUserId),
        name: `${firstName} ${lastName}`.trim(),
        email,
      })) || []
    const defaultPickupMiles = laneAlert?.pickupMiles ?? 100
    const defaultDeliveryMiles = laneAlert?.deliveryMiles ?? 100
    const {
      control,
      errors,
      formState,
      getValues,
      handleSubmit,
      register,
      reset,
      setValue,
      trigger,
    } = methods

    const dispatcherScope = useWatch<TAddLaneAlertModalSchema['dispatcherScope']>({
      control,
      name: 'dispatcherScope',
    })

    useEffect(() => {
      register('pickupCity')
      register('pickupStateCode')
      register('deliveryCity')
      register('deliveryStateCode')
      register('pickupStartDate')
      register('pickupEndDate')
      register('loadedMilesMin')
      register('loadedMilesMax')
      register('pickupIsoDaysOfWeek')
      register('subscriberParentIds')
    }, [register])

    const [pickupLocation, setPickupLocation] = useState<ILocationSuggestion>({
      city: pickupCity || specificLanePickupCity,
      state: pickupStateCode || specificLanePickupState,
    })
    const [deliveryLocation, setDeliveryLocation] = useState<ILocationSuggestion>({
      city: deliveryCity || specificLaneDeliveryCity,
      state: deliveryStateCode || specificLaneDeliveryState,
    })
    const [dropoffPref, setDropoffPref] = useState<DropoffPreference | undefined>(
      deriveDropoffPref(isAdd, loadedMilesMin, loadedMilesMax, specificLane !== undefined)
    )
    const [alwaysLooking, setAlwaysLooking] = useState(Boolean(pickupAvailability))
    const [specificDates, setSpecificDates] = useState(Boolean(pickupStartDate))
    const [dispatcher, setDispatcher] = useState<INameSearchResult>()
    const [dispatchers, setDispatchers] = useState<SubscribedDispatcher[]>(defaultSubscribers)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [disableAutoSuggestCarrier, setDisableAutoSuggestCarrier] = useState(true)
    const { carrier, setCarrier } = useCarrier({ carrierId, carrierName, carriers })

    const { mutate: addLaneAlert } = useCreateLaneAlert({ carrierId: Number(carrier?.id) })
    const { mutate: updateLaneAlert } = useUpdateCMLaneAlert({
      carrierId: Number(carrier?.id),
      id: laneAlert?.id || 0,
    })

    const [errorFetchMessage, setErrorFetchMesage] = useState<string | undefined>(undefined)
    useEffect(() => {
      if (!specificDates) {
        setValue('pickupStartDate', null)
        setValue('pickupEndDate', null)
        trigger('pickupStartDate')
      }
      if (carriers.length > 0 || carrier.id === '') {
        setDisableAutoSuggestCarrier(false)
      }
    }, [carrier, carriers, disableAutoSuggestCarrier, setValue, trigger, specificDates])

    /**
     * @description Update the subscriberParentIds field with the IDs of the selected dispatchers everytime the
     *              selection changes.
     */
    useEffect(() => {
      setValue(
        'subscriberParentIds',
        dispatchers.map(d => Number(d.id)),
        {
          shouldValidate: true,
        }
      )
    }, [dispatchers, setValue])

    /**
     * @description Clear the dipatchers array when the dispatcherScope field is set to DispatcherScope.allDispatchers.
     */
    useEffect(() => {
      if (dispatcherScope === DispatcherScope.allDispatchers) {
        setDispatchers([])
      }
    }, [dispatcherScope, setValue])

    const formatDate = (date: Date) => moment(date).utc().format(FORMATS.yearFirst)
    const onWeekdayChange = (day: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target
      let days = getValues('pickupIsoDaysOfWeek')
      days = days || []
      if (checked) {
        days.push(day)
      } else {
        const index = days.indexOf(day)
        days.splice(index, 1)
      }
      setValue('pickupIsoDaysOfWeek', days)
    }

    const onPickupChange = (value: ILocationSuggestion) => {
      setValue('pickupCity', value.city)
      setValue('pickupStateCode', value.state)
      setPickupLocation(value)
    }

    const onDeliveryChange = (value: ILocationSuggestion) => {
      setValue('deliveryCity', value.city)
      setValue('deliveryStateCode', value.state)
      setDeliveryLocation(value)
    }

    const createDropoffPrefChangeHandler = ({
      key,
      max = null,
      min = null,
    }: {
      key: DropoffPreference
      min?: number | null
      max?: number | null
    }) => () => {
      reset({
        ...getValues(),
        deliveryCity: null,
        deliveryStateCode: null,
        loadedMilesMin: min,
        loadedMilesMax: max,
      })
      setDropoffPref(key)
    }

    const handleAlwaysLooking = (value: boolean) => {
      const days = value ? [1, 2, 3, 4, 5, 6, 7] : null
      setValue('pickupIsoDaysOfWeek', days)
      setAlwaysLooking(value)
      trigger('pickupIsoDaysOfWeek')
    }

    const datesHandler = (dateArr: Date[]) => {
      const [start, end] = dateArr || []
      reset({
        ...getValues(),
        pickupStartDate: start ? formatDate(start) : null,
        pickupEndDate: end ? formatDate(end) : null,
      })
    }

    const addDispatcher = useCallback(() => {
      if (dispatcher) {
        setDispatchers(dispatchers => [...dispatchers, dispatcher])
        setDispatcher(undefined)
      }
    }, [dispatcher])

    const removeDispatcher = useCallback((idx: number) => {
      setDispatchers(dispatchers => dispatchers.filter((_, i) => idx !== i))
    }, [])

    const handleDispatcher = (value?: Partial<INameSearchResult>) => {
      const { email, id, name, type } = value || {}
      if (id && name && email) {
        setDispatcher({ id, name, email, type: type || '' })
      }
    }

    const onHandleError = () => {
      if (Object.keys(errors).length) {
        return 'Please fill out the required fields below'
      }

      return undefined
    }

    const onFormSubmit = (data: TAddLaneAlertModalSchema) => {
      const bookableShipmentSavedSearch = omit(
        {
          ...data,
          trailerType: data.trailerType === EquipmentType.any ? null : data.trailerType,
          subscribeEveryone: dispatcherScope === DispatcherScope.allDispatchers,
        },
        ['dispatcherScope', 'dropoffPreference', 'pickupAvailability']
      )
      const submitFn = isAdd ? addLaneAlert : updateLaneAlert
      setIsSubmitting(true)
      submitFn({ bookableShipmentSavedSearch })
        .then(() => {
          onSuccess()
          closeModal()
        })
        .catch(err => {
          setErrorFetchMesage(err.message)
          setIsSubmitting(false)
        })
    }
    const actionLabel = isAdd ? 'Add Lane Alert' : 'Edit Lane Alert'

    const carrierOptions = carriers.map(({ id, name }, i) => ({
      id: id.toString(),
      name,
      groupIndex: i,
    }))

    return (
      <FormProvider {...methods}>
        <FormModal
          title={actionLabel}
          size={4}
          open
          onClose={closeModal}
          fullHeight
          actions={[
            {
              label: 'Cancel',
              type: 'dismiss',
              dataTest: 'add-lane-alert-dialog-cancel',
            },
            {
              label: actionLabel,
              type: 'confirm',
              disabled: isSubmitting || !formState.isValid,
              action: handleSubmit(onFormSubmit),
              dataTest: 'add-lane-alert-dialog-submit',
            },
          ]}
          dataTest='add-lane-alert-modal'
          error={errorFetchMessage !== undefined ? errorFetchMessage : onHandleError()}>
          <Box mt={2}>
            <InlineMessage
              fullWidth
              message='Enjoy more flexible preferences and distraction-free alerting (emails are only sent to the carrier, not to you).'
              title='Lane alerts are new and improved'
            />
          </Box>
          <Box mt={2} mb={1}>
            <Typography variant='subtitle1'>Carrier</Typography>
          </Box>
          <CarrierAutosuggest
            onChange={option => {
              const id = option === undefined || option.id === undefined ? '' : option.id
              const name = option?.text

              setCarrier({
                id,
                name,
              })
            }}
            value={{ ...carrier, id: carrier.id.toString() }}
            defaultValue={{ ...carrier, id: carrier.id.toString() }}
            carrierOptions={carrierOptions}
            disabled={disableAutoSuggestCarrier}
          />
          <Box mt={2}>
            <Typography variant='subtitle1'>Lane Alert Settings</Typography>
          </Box>
          <Box mb={2} mt={1} className='flex'>
            <Box className='w-100'>
              <SuggestLocation
                onChange={onPickupChange}
                value={pickupLocation}
                placeholder='Enter Pickup Location'
                dataTest='set-pickup-location'
                resultTypes={['City']}
                id='pickup-location'
                large
                errorMessage={errors?.pickupCity?.message}
                label='Pickup Location'
                required
              />
            </Box>
            <Box className='w-30' ml={2}>
              <ControlledSelect
                control={control}
                defaultValue={defaultPickupMiles}
                name='pickupMiles'
                SelectProps={{
                  label: 'Radius',
                  options: stopRadii,
                  large: true,
                  dataTest: 'pickup-miles',
                }}
              />
            </Box>
          </Box>
          <Typography variant='caption' className='required'>
            Dropoff Preference
          </Typography>
          {errors?.dropoffPreference && (
            <p className='validation-message text-error' data-test='dropoff-preference-error'>
              {errors.dropoffPreference.message}
            </p>
          )}
          <Radio
            id='dropoff-local-hauls'
            name='dropoffMiles'
            label={
              <>
                <span className='b'>Local Hauls</span> up to 100mi
              </>
            }
            checked={dropoffPref === DropoffPreference.localHaul}
            className='mt1'
            dataTest='local-haul'
            onChange={createDropoffPrefChangeHandler({
              key: DropoffPreference.localHaul,
              max: 100,
            })}
          />
          <Radio
            id='dropoff-short-hauls'
            name='dropoffMiles'
            label={
              <>
                <span className='b'>Short Hauls</span> 100mi - 300mi
              </>
            }
            checked={dropoffPref === DropoffPreference.shortHaul}
            className='mt1'
            dataTest='short-haul'
            onChange={createDropoffPrefChangeHandler({
              key: DropoffPreference.shortHaul,
              min: 100,
              max: 300,
            })}
          />
          <Radio
            id='dropoff-long-hauls'
            name='dropoffMiles'
            label={
              <>
                <span className='b'>Long Hauls</span> over 300mi
              </>
            }
            checked={dropoffPref === DropoffPreference.longHaul}
            className='mt1'
            dataTest='long-haul'
            onChange={createDropoffPrefChangeHandler({ key: DropoffPreference.longHaul, min: 300 })}
          />
          <Radio
            id='dropoff-specific-location'
            name='dropoffMiles'
            label={
              <>
                <span className='b'>Specific Dropoff Location</span>
              </>
            }
            checked={dropoffPref === DropoffPreference.specificLocation}
            className='mt1'
            dataTest='specific-location'
            onChange={createDropoffPrefChangeHandler({ key: DropoffPreference.specificLocation })}
          />
          {dropoffPref === DropoffPreference.specificLocation && (
            <Box className='flex' mb={2} alignContent='center' alignItems='center'>
              <Box className='w-100'>
                <SuggestLocation
                  onChange={onDeliveryChange}
                  value={deliveryLocation}
                  placeholder='Enter Delivery Location'
                  dataTest='set-delivery-location'
                  resultTypes={['City']}
                  id='delivery-location'
                  large
                  errorMessage={errors?.deliveryCity?.message}
                  label='Delivery Location'
                  required
                />
              </Box>
              <Box className='w-30' ml={2}>
                <ControlledSelect
                  control={control}
                  defaultValue={defaultDeliveryMiles}
                  name='deliveryMiles'
                  SelectProps={{
                    label: 'Radius',
                    options: stopRadii,
                    large: true,
                    dataTest: 'delivery-miles',
                  }}
                />
              </Box>
            </Box>
          )}
          <div className='required'>
            <Typography variant='caption'>Equipment Type</Typography>
          </div>
          {errors?.trailerType && (
            <p className='validation-message text-error' data-test='trailer-type-error'>
              {errors.trailerType.message}
            </p>
          )}

          <ControlledRadioGroupInput
            control={control}
            name='trailerType'
            options={trailerTypeOptions}
            RadioProps={{
              dataTest: 'trailer-type',
            }}
          />

          <Box mt={1} className='required'>
            <Typography variant='caption'>Pickup Availability</Typography>
          </Box>
          {errors?.pickupAvailability && (
            <p className='validation-message text-error' data-test='pickup-availability-error'>
              {errors.pickupAvailability.message}
            </p>
          )}
          <Checkbox
            name='specificDates'
            label='Specific pickup dates only'
            defaultChecked={specificDates}
            dataTest='specific-dates'
            onChange={(event: SyntheticEvent<HTMLInputElement>) =>
              setSpecificDates(event.currentTarget.checked)
            }
          />
          {specificDates && (
            <div className='ml5'>
              <RangeDatePicker
                dates={initialDateRange}
                dateFormat='m/d/Y'
                onDateChange={datesHandler}
                minDate={TODAY}
              />
            </div>
          )}
          <Checkbox
            name='daysOfWeek'
            label='Always looking for this lane'
            defaultChecked={alwaysLooking}
            dataTest='days-of-week'
            onChange={(event: SyntheticEvent<HTMLInputElement>) =>
              handleAlwaysLooking(event.currentTarget.checked)
            }
          />
          {alwaysLooking && (
            <Box ml={5}>
              {daysOfWeek.map((day, idx) => (
                <Checkbox
                  defaultChecked={pickupIsoDaysOfWeek?.includes(idx + 1) ?? true}
                  name={day}
                  label={day}
                  dataTest={day}
                  key={day}
                  onChange={onWeekdayChange(idx + 1)}
                />
              ))}
            </Box>
          )}
          <Box mt={2} pb={6}>
            <Typography variant='subtitle1'>Recipients</Typography>
            <p>Choose who to alert about new loads</p>
            <div className='required'>
              <Typography variant='caption'>Select an option</Typography>
            </div>
            {errors?.dispatcherScope && (
              <p className='validation-message text-error' data-test='dispatcher-scope-error'>
                {errors.dispatcherScope.message}
              </p>
            )}

            <ControlledRadioGroupInput
              control={control}
              name='dispatcherScope'
              options={dispatcherScopeOptions}
              RadioProps={{
                dataTest: 'dispatcher-scope',
              }}
            />

            {dispatcherScope === DispatcherScope.specificDispatchers && (
              <>
                {errors?.subscriberParentIds && (
                  <p className='validation-message text-error' data-test='subscriber-ids-error'>
                    {(errors?.subscriberParentIds as any)?.message}
                  </p>
                )}
                <Box display='flex' justifyContent='space-between'>
                  <Box width='80%'>
                    <AdminAutosuggest
                      icon='search'
                      id='dispatcher-owner-op-user2'
                      placeholder='Search users...'
                      value={dispatcher}
                      onChange={handleDispatcher}
                      carrierApplicationId={Number(carrier.id)}
                      useParentId
                      searchType={SEARCH_TYPE.Dispatcher}
                    />
                  </Box>
                  <Box width='20%' ml={1}>
                    <Button
                      size='large'
                      fullWidth
                      disabled={
                        !dispatcher || dispatchers.some(dispr => dispr.id === dispatcher.id)
                      }
                      onClick={addDispatcher}
                      data-test='add-dispatcher'>
                      Add
                    </Button>
                  </Box>
                </Box>
                {dispatchers.map((dispr, idx) => {
                  const { email, id, name } = dispr
                  const removeHandler = () => removeDispatcher(idx)
                  return (
                    <Box key={`dispatcher${id}`}>
                      <Box display='flex' mt={2}>
                        <Box width='80%'>
                          <Box>{name}</Box>
                          <Box className='text-secondary'>{email}</Box>
                        </Box>
                        <Box width='20%' ml={1}>
                          <Button
                            startIcon={<RemoveIcon />}
                            data-test={`remove-dispatcher-${id}`}
                            onClick={removeHandler}>
                            Remove
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )
                })}
              </>
            )}
          </Box>
        </FormModal>
      </FormProvider>
    )
  }
)
